import { forwardRef, useCallback } from 'react';
import { Controller, ControllerProps, FieldValues, Path } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import PhoneInput from 'react-phone-number-input';

import { FormHelperText, Stack, StackProps, StandardTextFieldProps, TextField } from '@mui/material';

import { FormErrorHelperText } from '../FormErrorHelperText';
import { CountrySelect, CountrySelectProps } from './CountrySelect';

export type PhoneNumberInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
  className?: string;
  inputProps?: Omit<StandardTextFieldProps, 'label' | 'inputRef' | 'type'>;
  countrySelectProps?: Pick<CountrySelectProps, 'className'>;
  StackProps?: StackProps;
};

export const PhoneNumberCustomInput = forwardRef<HTMLInputElement, StandardTextFieldProps>((props, ref) => {
  return (
    <TextField
      label={<FormattedMessage id="phone-number-input-label" defaultMessage="Phone number" />}
      inputRef={ref}
      type="tel"
      {...props}
    />
  );
});

export const PhoneNumberInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>
>({
  className,
  countrySelectProps,
  inputProps,
  StackProps,
  ...rest
}: PhoneNumberInputProps<TFieldValues, TName>) => {
  const handleOnChange = useCallback((onChange: (value: string | null) => void) => {
    return (value?: string) => onChange(value ?? null);
  }, []);

  return (
    <Controller
      render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
        <Stack {...StackProps}>
          <PhoneInput
            className={className}
            countrySelectComponent={CountrySelect}
            countrySelectProps={countrySelectProps}
            defaultCountry="US"
            international
            inputComponent={PhoneNumberCustomInput}
            numberInputProps={inputProps}
            limitMaxLength
            onBlur={onBlur}
            onChange={handleOnChange(onChange)}
            ref={ref}
            value={value}
          />
          <FormHelperText error={Boolean(error?.message)}>
            {/* Using "name" or "error.message" result in messages unhelpful to the user.
            Showing "Error with provided phone number" instead. */}
            <FormErrorHelperText error={error} fieldName={'provided phone number'} />
          </FormHelperText>
        </Stack>
      )}
      {...rest}
    />
  );
};
